import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { aboutData } from '@/mocks/about';

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d9n4ossit35ii3jdlogg';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    document.title = '关于我们 - 蘑菇影视 | 品牌介绍、口碑评价、联系方式';
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="phone_alt"]') as HTMLInputElement);
    if (honeypot && honeypot.value.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    try {
      const formData = new FormData(form);
      const response = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormError('');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请稍后重试');
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于蘑菇影视',
    description: aboutData.introduction,
    url: 'https://www.mogumovie.com/about',
    about: {
      '@type': 'Organization',
      name: aboutData.brandName,
      description: aboutData.introduction,
      foundingDate: aboutData.foundingYear,
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        email: aboutData.contact.email,
        telephone: aboutData.contact.phone,
      },
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: aboutData.faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">
        {JSON.stringify(aboutSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="px-4 md:px-6 lg:px-8 py-6">
            <div className="max-w-[1440px] mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', path: '/' },
                  { label: '关于我们' },
                ]}
              />

              <section className="mt-6">
                <div className="text-center mb-10">
                  <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-50 mb-3">
                    关于蘑菇影视
                  </h1>
                  <p className="text-sm md:text-base text-foreground-400 max-w-2xl mx-auto leading-relaxed">
                    {aboutData.introduction}
                  </p>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6 mb-14">
                  {aboutData.stats.map((stat, index) => (
                    <div
                      key={index}
                      className="text-center p-5 rounded-xl bg-background-100 border border-background-200/50"
                    >
                      <div className="text-2xl md:text-3xl font-heading font-bold text-accent-500 mb-1">
                        {stat.value}
                      </div>
                      <div className="text-xs text-foreground-500">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </section>

              <section className="mb-14">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-100 mb-6 text-center">
                  核心价值观
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {aboutData.values.map((value, index) => (
                    <div
                      key={index}
                      className="p-5 rounded-xl bg-background-100 border border-background-200/50 hover:border-primary-500/20 transition-all duration-300"
                    >
                      <h3 className="text-sm font-semibold text-foreground-100 mb-2">{value.title}</h3>
                      <p className="text-xs text-foreground-500 leading-relaxed">{value.description}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section className="mb-14">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-100 mb-6 text-center">
                  发展历程
                </h2>
                <div className="max-w-3xl mx-auto relative">
                  <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-200/50 md:-translate-x-px" />
                  <div className="space-y-6">
                    {aboutData.milestones.map((milestone, index) => (
                      <div
                        key={index}
                        className={`relative flex items-start gap-6 ${
                          index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                        }`}
                      >
                        <div className="md:w-1/2 flex items-start gap-4 md:justify-end">
                          <div className={`flex items-center gap-4 ${index % 2 === 1 ? 'md:flex-row-reverse md:text-right' : ''}`}>
                            <div className="flex-shrink-0 w-4 h-4 rounded-full bg-accent-500 border-2 border-background-50 relative z-10 mt-1" />
                            <div className="p-4 rounded-xl bg-background-100 border border-background-200/50">
                              <span className="text-xs font-bold text-accent-400">{milestone.year}</span>
                              <p className="text-sm text-foreground-300 mt-1">{milestone.event}</p>
                            </div>
                          </div>
                        </div>
                        <div className="hidden md:block md:w-1/2" />
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              <section className="mb-14">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-100 mb-6 text-center">
                  用户口碑
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {aboutData.testimonials.map((testimonial, index) => (
                    <div
                      key={index}
                      className="p-5 rounded-xl bg-background-100 border border-background-200/50"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <img
                          src={testimonial.avatar}
                          alt={testimonial.name}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                        <div>
                          <div className="text-sm font-medium text-foreground-100">{testimonial.name}</div>
                          <div className="flex items-center gap-0.5">
                            {Array.from({ length: 5 }).map((_, i) => (
                              <div key={i} className="w-3 h-3 flex items-center justify-center">
                                <i
                                  className={`text-[10px] ${
                                    i < testimonial.rating
                                      ? 'ri-star-fill text-accent-500'
                                      : 'ri-star-line text-foreground-600'
                                  }`}
                                ></i>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                      <p className="text-xs text-foreground-400 leading-relaxed">{testimonial.comment}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section className="mb-14">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-100 mb-6 text-center">
                  常见问题
                </h2>
                <div className="max-w-3xl mx-auto space-y-3">
                  {aboutData.faqs.map((faq, index) => (
                    <details
                      key={index}
                      className="group rounded-xl bg-background-100 border border-background-200/50 overflow-hidden"
                    >
                      <summary className="flex items-center justify-between p-4 cursor-pointer list-none">
                        <h3 className="text-sm font-medium text-foreground-200 pr-4">{faq.question}</h3>
                        <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center">
                          <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-200"></i>
                        </div>
                      </summary>
                      <div className="px-4 pb-4">
                        <p className="text-sm text-foreground-400 leading-relaxed">{faq.answer}</p>
                      </div>
                    </details>
                  ))}
                </div>
              </section>

              <section id="contact" className="pb-12">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-100 mb-6 text-center">
                  联系我们
                </h2>
                <div className="max-w-4xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="space-y-4">
                    <div className="flex items-start gap-3 p-4 rounded-xl bg-background-100 border border-background-200/50">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-500/10">
                        <i className="ri-mail-line text-lg text-primary-500"></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground-100">电子邮箱</h4>
                        <p className="text-sm text-foreground-400 mt-0.5">{aboutData.contact.email}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-4 rounded-xl bg-background-100 border border-background-200/50">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-500/10">
                        <i className="ri-phone-line text-lg text-primary-500"></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground-100">客服电话</h4>
                        <p className="text-sm text-foreground-400 mt-0.5">{aboutData.contact.phone}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-4 rounded-xl bg-background-100 border border-background-200/50">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-500/10">
                        <i className="ri-map-pin-line text-lg text-primary-500"></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground-100">公司地址</h4>
                        <p className="text-sm text-foreground-400 mt-0.5">{aboutData.contact.address}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-4 rounded-xl bg-background-100 border border-background-200/50">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-500/10">
                        <i className="ri-time-line text-lg text-primary-500"></i>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-foreground-100">工作时间</h4>
                        <p className="text-sm text-foreground-400 mt-0.5">{aboutData.contact.workingHours}</p>
                      </div>
                    </div>
                  </div>

                  <div className="p-5 rounded-xl bg-background-100 border border-background-200/50">
                    <h3 className="text-sm font-semibold text-foreground-100 mb-4">发送消息</h3>
                    <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <label htmlFor="contact-name" className="block text-xs text-foreground-400 mb-1.5">姓名</label>
                        <input
                          id="contact-name"
                          name="name"
                          type="text"
                          required
                          className="w-full px-3 py-2 text-sm bg-background-200/50 border border-background-300/50 rounded-lg text-foreground-100 placeholder-foreground-500 focus:outline-none focus:border-primary-500/50 transition-all duration-200"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-xs text-foreground-400 mb-1.5">邮箱</label>
                        <input
                          id="contact-email"
                          name="email"
                          type="email"
                          required
                          className="w-full px-3 py-2 text-sm bg-background-200/50 border border-background-300/50 rounded-lg text-foreground-100 placeholder-foreground-500 focus:outline-none focus:border-primary-500/50 transition-all duration-200"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-message" className="block text-xs text-foreground-400 mb-1.5">留言内容</label>
                        <textarea
                          id="contact-message"
                          name="message"
                          required
                          maxLength={500}
                          rows={4}
                          className="w-full px-3 py-2 text-sm bg-background-200/50 border border-background-300/50 rounded-lg text-foreground-100 placeholder-foreground-500 focus:outline-none focus:border-primary-500/50 transition-all duration-200 resize-none"
                          placeholder="请输入您的留言内容（500字以内）"
                        ></textarea>
                      </div>

                      <input
                        type="text"
                        name="phone_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                        className="honeypot-field"
                      />

                      <button
                        type="submit"
                        className="w-full py-2.5 bg-primary-500 hover:bg-primary-600 text-foreground-50 text-sm font-medium rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
                      >
                        发送消息
                      </button>

                      {formStatus === 'success' && (
                        <p className="text-xs text-green-400 text-center">消息已发送成功！我们会尽快回复您。</p>
                      )}
                      {formStatus === 'error' && formError && (
                        <p className="text-xs text-primary-400 text-center">{formError}</p>
                      )}
                    </form>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}